import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  acceptCoverageRequest,
  declineCoverageRequest,
  fetchCoverageRequest,
  fetchCoverageRequests,
} from "../api/coverageRequestApi";
import type { CoverageRequestQuery } from "./types";

export const coverageRequestKeys = {
  all: ["coverage-requests"] as const,
  list: (query: CoverageRequestQuery) =>
    [...coverageRequestKeys.all, "list", query] as const,
  detail: (id: number) => [...coverageRequestKeys.all, "detail", id] as const,
};

export function useCoverageRequests(
  query: CoverageRequestQuery = {},
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: coverageRequestKeys.list(query),
    queryFn: () => fetchCoverageRequests(query),
    enabled: options.enabled ?? true,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

export function useCoverageRequest(id: number | null) {
  return useQuery({
    queryKey: coverageRequestKeys.detail(id ?? 0),
    queryFn: () => fetchCoverageRequest(id!),
    enabled: id !== null,
    staleTime: 30_000,
  });
}

export function useAcceptCoverageRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => acceptCoverageRequest(id),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: coverageRequestKeys.all });
    },
  });
}

export function useDeclineCoverageRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) =>
      declineCoverageRequest(id, reason),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: coverageRequestKeys.all });
    },
  });
}
